import type { Chart, MetricType } from 'features/charts-v2/types'
import type {
  TransformedChartData,
  TransformedDataPoint,
} from 'features/charts-v2/types/chart-strategy'

/**
 * Calculate the maximum Y value from chart data
 * Handles both single and stacked values
 */
export function calculateYMax(chartData: Chart | null): number | undefined {
  if (!chartData || !chartData.data || chartData.data.length === 0) {
    return undefined
  }

  let maxValue = 0

  for (const dataPoint of chartData.data) {
    if (!dataPoint.values || dataPoint.values.length === 0) continue

    // For stacked charts, sum all values in the stack. Non-finite entries
    // (NaN / ±Infinity — the type permits them and a bad backend division can
    // emit them) are treated as 0 so a single poisoned cell can't turn the
    // whole axis max into NaN and hand Recharts a NaN Y-domain.
    const totalValue = dataPoint.values.reduce(
      (sum: number, val) => sum + (Number.isFinite(val) ? (val as number) : 0),
      0,
    )
    maxValue = Math.max(maxValue, totalValue)

    // Also check individual values (for non-stacked or dual-axis charts)
    for (const val of dataPoint.values) {
      if (Number.isFinite(val)) {
        maxValue = Math.max(maxValue, val as number)
      }
    }
  }

  // Return raw max - rounding in AxisSyncContext provides natural padding via ceiling
  return maxValue
}

/**
 * Calculate axis range for scatter/bubble charts
 * Uses values[valueIndex] to get the metric value
 * Returns [min, max] tuple
 *
 * @param chartData - Chart data containing data points
 * @param valueIndex - Index in values array (0 for X-axis, 1 for Y-axis, etc.)
 */
export function calculateScatterRange(
  chartData: Chart | null,
  valueIndex: number,
): [number, number] | undefined {
  if (!chartData || !chartData.data || chartData.data.length === 0) {
    return undefined
  }

  let minValue = Infinity
  let maxValue = -Infinity

  for (const dataPoint of chartData.data) {
    if (!dataPoint.values || dataPoint.values.length <= valueIndex) continue

    // Only finite values participate in the range. NaN / ±Infinity (type-legal
    // and emittable by a bad backend division) would otherwise poison
    // Math.min/Math.max and flow on as a NaN axis domain, since Recharts honors
    // an explicit (non-stacked) domain verbatim without sanitizing it.
    const value = dataPoint.values[valueIndex]
    if (Number.isFinite(value)) {
      minValue = Math.min(minValue, value as number)
      maxValue = Math.max(maxValue, value as number)
    }
  }

  if (minValue === Infinity || maxValue === -Infinity) {
    return undefined
  }

  return [minValue, maxValue]
}

/**
 * Round up to a clean axis value based on magnitude
 * Uses ceil(value / step) * step where step is derived from magnitude
 *
 * Example: 22648 -> magnitude=10000, step=1000 -> ceil(22.648)*1000 = 23000
 */
export function roundUpToCleanAxisValue(value: number): number {
  if (value === 0) return 0
  if (value < 0) return -roundUpToCleanAxisValue(-value)

  // Find the order of magnitude
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)))

  // Step is magnitude / 10 for fine granularity
  const step = magnitude / 10

  // Round up to the nearest step
  return Math.ceil(value / step) * step
}

/**
 * Round down to a clean axis value based on magnitude
 * Uses floor(value / step) * step where step is derived from magnitude
 *
 * Example: 487 -> magnitude=100, step=10 -> floor(48.7)*10 = 480
 */
export function roundDownToCleanAxisValue(value: number): number {
  if (value === 0) return 0
  if (value < 0) return -roundDownToCleanAxisValue(-value)

  // Find the order of magnitude
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)))

  // Step is magnitude / 10 for fine granularity
  const step = magnitude / 10

  // Round down to the nearest step
  return Math.floor(value / step) * step
}

/**
 * Compute padded scatter/bubble chart axis domain from data range
 *
 * Matches V1 bubble chart logic exactly:
 * - Padding: range/10 (or max/5 if range is 0)
 * - Rounding: to 2 decimal places
 * - Single-point sentiment: [-1, 1]
 * - Single-point rating: [0, 5]
 * - No metric clamping for multi-point cases
 *
 * @param dataRange - Raw [min, max] from data, or undefined if no data
 * @param metricType - Optional metric type for special single-point handling
 * @returns Padded domain [min, max] rounded to 2 decimals, or undefined if no range
 */
export function computePaddedScatterDomain(
  dataRange: [number, number] | undefined,
  metricType?: MetricType,
): [number, number] | undefined {
  if (!dataRange) {
    return undefined
  }

  const [dataMin, dataMax] = dataRange

  // Single data point (all points have the same value)
  if (dataMin === dataMax) {
    if (metricType === 'sentiment') {
      return [-1, 1]
    }
    if (metricType === 'rating') {
      return [0, 5] // V1 uses 0, not 1
    }
    // For other metrics, use max/5 as offset (V1 fallback)
    const offset = Math.abs(dataMax) / 5 || 0.1
    return [
      Math.round((dataMin - offset) * 100) / 100,
      Math.round((dataMax + offset) * 100) / 100,
    ]
  }

  // Multiple points: V1 uses range/10 as padding
  const offset = (dataMax - dataMin) / 10

  // Round to 2 decimal places (V1 behavior)
  let paddedMin = Math.round((dataMin - offset) * 100) / 100
  let paddedMax = Math.round((dataMax + offset) * 100) / 100

  // Clamp to metric bounds to prevent axis overshoot
  if (metricType === 'sentiment') {
    paddedMin = Math.max(paddedMin, -1)
    paddedMax = Math.min(paddedMax, 1)
  } else if (metricType === 'rating') {
    paddedMin = Math.max(paddedMin, 0)
    paddedMax = Math.min(paddedMax, 5)
  }

  return [paddedMin, paddedMax]
}

/**
 * Snap step + scale bounds for fractional-metric bar value axes.
 * Rating snaps to whole units on the 0–5 scale; sentiment snaps to
 * quarter units on the -1–1 scale.
 */
const BAR_SNAP_BY_METRIC = {
  rating: { step: 1, scaleMin: 0, scaleMax: 5 },
  sentiment: { step: 0.25, scaleMin: -1, scaleMax: 1 },
} as const

/**
 * Snap a fractional-metric bar chart's value-axis range OUTWARD to the
 * metric's natural steps, then drop the floor ONE FULL STEP BELOW the snapped
 * minimum, clamped to the metric scale.
 *
 * Replaces the snug `range/10` padded domains for bars (user decision at the
 * PROD-3724 preview gate): rating values 4.58–4.61 → [3, 5]; 3.2–4.7 →
 * [2, 5]; sentiment 0.42–0.47 → [0, 0.5]. Scatter/bubble keep
 * `computePaddedScatterDomain`'s padded behavior — do not reuse this there.
 *
 * The step of headroom below the minimum is what keeps the SHORTEST bar
 * drawable (PROD-5286, user decision: "expand one step below the min").
 * Recharts bases a bar at 0 clamped into the domain, so a value sitting
 * exactly ON the floor draws a zero-length rect — which recharts then omits
 * from the DOM entirely, leaving nothing for the click handler on `<Bar>` to
 * hit. That is how a rating of exactly 3.0 on a [3, 5] axis became an
 * unclickable "missing" bar that still rendered its row highlight, tooltip
 * and value label. Snapping alone could not prevent it: `floor(3.0)` IS 3.
 * With the headroom, no data value can ever land on the floor.
 *
 * Equal-value ranges are subsumed: all bars 4.6 → [3, 5] naturally. Clamping
 * to the scale CAN re-collapse the domain when the data sits on the scale
 * edge (every bar at sentiment -1 → floor clamps back up to -1), so the
 * degenerate case is caught after clamping and given one step of range: the
 * domain is never degenerate (b > a always — the headline guarantee of
 * PROD-3724).
 */
export function computeSnappedBarDomain(
  range: [number, number] | undefined,
  metricType: 'rating' | 'sentiment',
): [number, number] | undefined {
  if (!range) {
    return undefined
  }

  const { step, scaleMin, scaleMax } = BAR_SNAP_BY_METRIC[metricType]
  const snappedMin = Math.floor(range[0] / step) * step - step
  const snappedMax = Math.ceil(range[1] / step) * step

  const domainMin = Math.max(snappedMin, scaleMin)
  let domainMax = Math.min(snappedMax, scaleMax)

  // Data on the scale edge: clamping pulled the floor back onto the value, so
  // give the domain one step of range upward instead.
  if (domainMin === domainMax) {
    domainMax = Math.min(domainMin + step, scaleMax)
  }

  return [domainMin, domainMax]
}

/**
 * Guard a bar chart's resolved value-axis range against degenerate
 * (zero-width) domains before it reaches Recharts.
 *
 * Recharts honors an explicit `domain={[min, max]}` verbatim and has no
 * degenerate-domain handling, so `[v, v]` (all bars equal — including the
 * common single-bar chart) renders a broken zero-width scale. Chart.js (the
 * V1 engine) applies a universal zero-range expansion for every dataset;
 * this is the equivalent guard for the hand-rolled V2 pipeline.
 *
 * - Distinct ranges (`min !== max`) pass through untouched, EXCEPT that a count
 *   metric's POSITIVE floor is pulled to 0. Cross-panel sync hands back
 *   `[globalXMin, globalXMax]` (`AxisSyncContext.getSyncedDomain`), so with
 *   "Sync X-axis" on — the default — a count axis floored at the smallest bar's
 *   value drew that bar as a sliver: counts 10,100 and 12,000 became 22px and
 *   451px, a 1.2:1 ratio drawn 20:1 (PROD-5286). Counts are length-encoded from
 *   zero, so 0 is the only correct floor; the stacked-horizontal registration
 *   in Container already registers `xMin: 0` for the same reason.
 * - Count metrics expand zero-based: `[0, roundUpToCleanAxisValue(1.2 × v) || 1]`
 *   (counts are never negative; zero-based is the bar-chart convention and
 *   matches the sync-off `[0, roundUp(max)]` shape).
 * - Sentiment/rating snap outward via `computeSnappedBarDomain`, whose floor
 *   sits one step below the snapped minimum (all bars 4.6 → [3, 5]; all bars
 *   0.5 → [0.25, 0.5]).
 * - Other metrics delegate to `computePaddedScatterDomain`'s existing
 *   equal-value branch (fractional fallback → ±max/5 padding).
 *
 * Pure function of (range, metricType): synced panels sharing a range
 * resolve identical guarded domains.
 */
export function resolveBarValueDomain(
  range: [number, number] | undefined,
  metricType?: MetricType,
): [number, number] | undefined {
  if (!range) {
    return undefined
  }

  const [min, max] = range
  const isCountMetric =
    metricType === 'record_count' || metricType === 'sentence_count'

  if (min !== max) {
    // `min > 0`, not `min !== 0`: a negative floor must survive. Counts should
    // never be negative, but `computeNiceLineDomain` guards its own count floor
    // with `dataMin >= 0` and is tested for it, so the bar path holds the same
    // line — zero-flooring a [-5, 20] range would push the -5 bar outside the
    // domain and recharts would silently clip it away. (`min === 0` needs no
    // branch either: [0, max] IS the range.)
    return isCountMetric && min > 0 ? [0, max] : range
  }

  if (isCountMetric) {
    return [0, roundUpToCleanAxisValue(max * 1.2) || 1]
  }

  if (metricType === 'rating' || metricType === 'sentiment') {
    return computeSnappedBarDomain(range, metricType)
  }

  return computePaddedScatterDomain(range, metricType)
}

/**
 * Quantize fractional-metric bar values to display precision (2 decimal
 * places) so bars are plotted at exactly the value every displayed number
 * (tick labels, bar value labels) claims. On the deliberately snug padded
 * bar domains, sub-hundredth raw differences (e.g. 4.6234 vs 4.6199, both
 * labeled "4.62") otherwise render as visibly different bar lengths that
 * miss their labeled gridline.
 *
 * No-op for count-only metrics (integer values) — returns the input
 * unchanged. For fractional metrics, every numeric field of every row is
 * rounded to 2dp (integer fields like volumeCount round-trip exactly).
 */
export function quantizeBarValuesToDisplayPrecision(
  transformed: TransformedChartData,
  selectedMetrics: MetricType[],
): TransformedChartData {
  const hasFractionalMetric = selectedMetrics.some(
    (m) => m === 'sentiment' || m === 'rating' || m === 'net_promoter_score',
  )
  if (!hasFractionalMetric || !transformed.chartData) {
    return transformed
  }

  return {
    ...transformed,
    chartData: transformed.chartData.map((row) => {
      const quantized: TransformedDataPoint = {}
      for (const [key, value] of Object.entries(row)) {
        quantized[key] =
          typeof value === 'number' ? Math.round(value * 100) / 100 : value
      }
      return quantized
    }),
  }
}

/**
 * Chart.js's "nice number" algorithm
 * Rounds a number to a "nice" value from the set {1, 2, 5} x 10^n
 *
 * @param range - The input value to round
 * @param round - If true, round to nearest nice number; if false, round up (ceil)
 * @returns Nice number closest to the input
 */
export function niceNum(range: number, round: boolean): number {
  const exponent = Math.floor(Math.log10(range))
  const fraction = range / Math.pow(10, exponent)
  let niceFraction: number

  if (round) {
    if (fraction < 1.5) niceFraction = 1
    else if (fraction < 3) niceFraction = 2
    else if (fraction < 7) niceFraction = 5
    else niceFraction = 10
  } else {
    if (fraction <= 1) niceFraction = 1
    else if (fraction <= 2) niceFraction = 2
    else if (fraction <= 5) niceFraction = 5
    else niceFraction = 10
  }

  return niceFraction * Math.pow(10, exponent)
}

/**
 * Generate explicit tick arrays for scatter/bubble charts.
 *
 * Uses Chart.js's niceNum algorithm to pick step sizes from {1, 2, 5} × 10^n,
 * snaps the domain to nice boundaries, and clamps to metric bounds when the
 * metric has a fixed range (rating, sentiment).
 *
 * @param domainMin - Minimum value of the axis domain
 * @param domainMax - Maximum value of the axis domain
 * @param metricType - Optional metric type for clamping (e.g., rating max 5, sentiment [-1, 1])
 * @returns Array of tick values
 */
export function generateScatterTicks(
  domainMin: number,
  domainMax: number,
  metricType?: MetricType,
): number[] {
  const range = domainMax - domainMin

  // Zero-width (or inverted) range: niceNum would take log10(0) = -Infinity
  // and emit NaN ticks. A single tick at the value is the only honest axis.
  if (range <= 0) {
    return [Math.round(domainMin * 100) / 100]
  }

  const niceRange = niceNum(range, false)
  // Floor the step at 0.01: ticks are snapped to 2 decimal places below, so
  // any finer step would emit duplicate tick values on near-zero ranges.
  const step = Math.max(niceNum(niceRange / 10, true), 0.01)

  let extendedMin = Math.floor(domainMin / step) * step
  let extendedMax = Math.ceil(domainMax / step) * step

  if (metricType === 'sentiment') {
    extendedMin = Math.max(extendedMin, -1)
    extendedMax = Math.min(extendedMax, 1)
  } else if (metricType === 'rating') {
    extendedMin = Math.max(extendedMin, 0)
    extendedMax = Math.min(extendedMax, 5)
  }

  const ticks: number[] = []
  let current = extendedMin
  while (current <= extendedMax + step / 2) {
    ticks.push(Math.round(current * 100) / 100)
    current += step
  }

  if (metricType === 'sentiment') {
    return [...new Set(ticks.map((t) => Math.max(-1, Math.min(1, t))))]
  }
  if (metricType === 'rating') {
    return [...new Set(ticks.map((t) => Math.max(0, Math.min(5, t))))]
  }
  return ticks
}

/**
 * Format axis tick values to match V1's numberFormatter behavior.
 * Drops trailing zeros: 4.00 -> "4", 3.20 -> "3.2", 0.17 -> "0.17".
 * Adds thousands separators: 1000 -> "1,000".
 *
 * For integer-valued metrics (record_count, sentence_count), floors the value
 * so averages render as whole numbers — e.g. 17412.62 -> "17,412".
 */
export function formatScatterAxisTick(
  value: number,
  metricType?: MetricType,
): string {
  if (metricType === 'record_count' || metricType === 'sentence_count') {
    return Math.floor(value).toLocaleString()
  }
  return Number(Number(value).toFixed(2)).toLocaleString()
}

/**
 * Helper to calculate decimal places needed for a number
 * Matches Chart.js _decimalPlaces helper
 */
function decimalPlaces(value: number): number {
  if (!isFinite(value) || value === 0) {
    return 0
  }
  let e = 1
  let p = 0
  while (Math.round(value * e) / e !== value) {
    e *= 10
    p++
    if (p > 20) break // Prevent infinite loop
  }
  return p
}

/**
 * Compute nice domain and ticks for line/area charts
 * Matches V1 Chart.js behavior exactly from generateTicks source with count parameter
 *
 * Algorithm (from Chart.js source - Case 3 when count is defined):
 * 1. spacing = niceNum(range / maxSpaces) where maxSpaces = maxTicks - 1
 * 2. niceMin = floor(dataMin / spacing) * spacing
 * 3. niceMax = ceil(dataMax / spacing) * spacing
 * 4. numSpaces = count - 1
 * 5. spacing = (niceMax - niceMin) / numSpaces  // redistribute to exact count
 * 6. Generate ticks: niceMin + j*spacing, rounded to appropriate decimal places
 *
 * V1 uses count: 9 in line.ts, so we target 9 ticks across nice boundaries.
 * V1 also applies sentiment expansion: if range < 0.3, expand to 0.3 around center.
 *
 * @param dataRange - Raw [min, max] from data, or undefined if no data
 * @param metricType - Optional metric type for special handling and clamping
 * @param count - Target number of ticks (default 9, matching V1)
 * @returns Object with domain [min, max] and ticks array, or undefined if no range
 */
export function computeNiceLineDomain(
  dataRange: [number, number] | undefined,
  metricType?: MetricType,
  count: number = 9,
): { domain: [number, number]; ticks: number[] } | undefined {
  if (!dataRange) {
    return undefined
  }

  let [dataMin, dataMax] = dataRange

  // A non-finite bound (NaN / ±Infinity) would propagate through niceNum's
  // log10 into a [NaN, NaN] domain and an all-NaN ticks array, which
  // LineChartComponents passes to Recharts' YAxis verbatim (explicit domain +
  // ticks are honored without sanitizing) — producing the NaN gridline/tick
  // coordinates this guards against. Bail so the axis falls back to Recharts'
  // NaN-safe auto-scale. The sentiment/rating call site extracts values with a
  // `typeof v === 'number'` filter that lets NaN through, so this is the
  // chokepoint that closes that leak too.
  if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) {
    return undefined
  }

  const isNonNegativeCount =
    (metricType === 'record_count' || metricType === 'sentence_count') &&
    dataMin >= 0

  // V1 sentiment expansion: if range < 0.3, expand to 0.3 around center
  if (metricType === 'sentiment') {
    const range = dataMax - dataMin
    if (range < 0.3) {
      const center = (dataMax + dataMin) / 2
      dataMin = Math.max(center - 0.15, -1) // Half of minimum range
      dataMax = Math.min(center + 0.15, 1)
    }
  }

  const range = dataMax - dataMin

  // If range is 0, use metric-specific defaults
  if (range === 0) {
    if (metricType === 'sentiment') {
      return {
        domain: [-1, 1],
        ticks: [-1, -0.75, -0.5, -0.25, 0, 0.25, 0.5, 0.75, 1],
      }
    }
    if (metricType === 'rating') {
      return {
        domain: [1, 5],
        ticks: [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5],
      }
    }
    if (isNonNegativeCount) {
      const domainMax = dataMax + (Math.abs(dataMax) / 5 || 0.1)
      return {
        domain: [0, domainMax],
        ticks: [0, domainMax],
      }
    }
    // For other metrics, use a small offset
    const offset = Math.abs(dataMax) / 5 || 0.1
    const domainMin = dataMin - offset
    const domainMax = dataMax + offset
    return {
      domain: [domainMin, domainMax],
      ticks: [domainMin, dataMin, dataMax, domainMax],
    }
  }

  // Chart.js algorithm (Case 3: count is defined)
  // Step 1: Initial spacing based on nice number of range/maxSpaces
  const maxTicks = 11 // Chart.js default maxTicksLimit
  const maxSpaces = maxTicks - 1
  let spacing = niceNum(range / maxSpaces, false)

  // Step 2-3: Compute nice boundaries
  let niceMin = Math.floor(dataMin / spacing) * spacing
  let niceMax = Math.ceil(dataMax / spacing) * spacing

  // Step 3b: Extend by 2 spacing steps for buffer ticks above and below data
  niceMin -= 2 * spacing
  niceMax += 2 * spacing

  // Step 4-5: Redistribute to exact count
  const numSpaces = count - 1
  spacing = (niceMax - niceMin) / numSpaces

  // Clamp nice boundaries to metric bounds before generating ticks
  if (metricType === 'sentiment') {
    niceMin = Math.max(niceMin, -1)
    niceMax = Math.min(niceMax, 1)
    // Recalculate spacing after clamping
    spacing = (niceMax - niceMin) / numSpaces
  } else if (metricType === 'rating') {
    niceMin = Math.max(niceMin, 1)
    niceMax = Math.min(niceMax, 5)
    // Recalculate spacing after clamping
    spacing = (niceMax - niceMin) / numSpaces
  } else if (isNonNegativeCount) {
    niceMin = 0
    spacing = (niceMax - niceMin) / numSpaces
  }

  // Step 6: Generate ticks
  // Calculate decimal places needed (matching Chart.js _decimalPlaces)
  const decimalPlacesNeeded = Math.max(
    decimalPlaces(spacing),
    decimalPlaces(niceMin),
  )
  const factor = Math.pow(10, decimalPlacesNeeded)

  const ticks: number[] = []
  for (let j = 0; j <= numSpaces; j++) {
    const tickValue = Math.round((niceMin + j * spacing) * factor) / factor
    ticks.push(tickValue)
  }

  // Final clamp for ticks to metric bounds (in case of floating point drift)
  if (metricType === 'sentiment') {
    const clampedTicks = ticks.map((t) => Math.max(-1, Math.min(1, t)))
    return { domain: [niceMin, niceMax], ticks: clampedTicks }
  }
  if (metricType === 'rating') {
    const clampedTicks = ticks.map((t) => Math.max(1, Math.min(5, t)))
    return { domain: [niceMin, niceMax], ticks: clampedTicks }
  }

  return { domain: [niceMin, niceMax], ticks }
}

/**
 * Maximum rendered bar length for basic/stacked bar charts.
 *
 * Mirrors StackedBarChart's category derivation:
 *   `breakdownCategories ?? ['count']`
 * and sums those segment keys per transformed row — the actual drawn bar
 * length. Unlike `calculateYMax`, which sums each raw ChartValue row
 * independently (wrong for stacked bars where rows are per-(group,segment)
 * pairs, not per-bar), this sums the transformed per-group totals the bars
 * actually stack, so the axis fits the visible bars after legend filtering.
 */
export function calculateMaxBarTotal(
  transformed: TransformedChartData | null | undefined,
): number | undefined {
  const rows = transformed?.chartData
  if (!rows || rows.length === 0) return undefined
  const categories = transformed?.metadata?.breakdownCategories?.length
    ? transformed.metadata.breakdownCategories
    : ['count']
  let max = 0
  for (const row of rows) {
    let total = 0
    for (const c of categories) {
      const v = row[c]
      if (typeof v === 'number') total += v
    }
    max = Math.max(max, total)
  }
  return max
}
